import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Target, Zap, Users, TrendingUp, CheckCircle2, BarChart2, Settings, Layers, ExternalLink, Download } from "lucide-react";

const CAL_URL = "https://cal.com/jigesh-shah-ryvr/30min";

const COMPANY_TYPES = [
  "B2B SaaS / Product", "AI Company", "B2B Tech Services",
  "B2B Manufacturing", "B2B Professional Services",
  "B2C Product", "B2C Digital", "D2C",
];

const SERVICES = [
  {
    icon: Target,
    color: "#7C3AED",
    title: "RevOps Strategy",
    tagline: "We define the revenue architecture before a single line of configuration is written.",
    deliverables: [
      "Revenue architecture & roadmap",
      "ICP, buyer journey & lifecycle stage mapping",
      "Lead scoring & pipeline stage definitions",
      "Data model design (objects, properties, relationships)",
      "KPI framework & reporting requirements",
      "Tech stack audit & integration blueprint",
    ],
  },
  {
    icon: Settings,
    color: "#00B4A0",
    title: "Systems & Execution",
    tagline: "We build the CRM, automation, integrations and reporting infrastructure — configured to match the strategy.",
    deliverables: [
      "HubSpot / Salesforce portal setup & configuration",
      "Custom objects, properties & pipeline build",
      "Workflow & automation implementation",
      "Third-party integrations (MAP, ERP, data tools)",
      "Data migration, cleansing & enrichment",
      "Dashboard & reporting build-out",
    ],
  },
  {
    icon: Layers,
    color: "#F59E0B",
    title: "Embedded Delivery",
    tagline: "We stay inside the engagement — sprint governance, QA, adoption, iteration. The strategy runs in the system.",
    deliverables: [
      "Sprint planning & delivery governance",
      "QA & alignment checks vs. RevOps strategy",
      "Stakeholder communication & status reporting",
      "Team enablement & adoption support",
      "Change management & iteration guidance",
      "Ongoing performance review & optimisation",
    ],
  },
];

const ENGAGEMENTS = [
  {
    num: "01",
    title: "Greenfield Build",
    sub: "Start from zero. Build for scale.",
    when: "No CRM in place, or a broken/abandoned one. Client needs a full RevOps foundation from scratch.",
    what: ["Full RevOps strategy & GTM architecture", "CRM selection, portal build & configuration", "Lifecycle, pipeline, scoring & workflow setup", "Guided delivery with RYVR embedded throughout"],
    color: "#7C3AED",
  },
  {
    num: "02",
    title: "Maximise Existing Investment",
    sub: "You have the tools. Let's unlock their full value.",
    when: "CRM & tech stack already in place but underutilised, siloed, or misaligned to current GTM motion.",
    what: ["RevOps audit — gaps, waste & quick wins", "Re-architecture & integration optimisation", "Reporting, attribution & pipeline visibility", "Ongoing strategic advisory with hands-on support"],
    color: "#00B4A0",
  },
  {
    num: "03",
    title: "ABM-Led Growth",
    sub: "Target smarter. Engage deeper. Convert faster.",
    when: "Client needs account-based motion layered on top of CRM — list intelligence, multi-channel engagement & creative campaigns.",
    what: ["List building via Clay.com — enriched, scored ICP accounts", "Engagement via Prospectbase — awareness & profiled leads", "Creative campaigns — theme, messaging & content strategy", "CRM-integrated ABM workflows & attribution tracking"],
    color: "#F59E0B",
  },
];

const CASE_STUDIES = [
  {
    client: "Global Consulting Company",
    type: "Greenfield Build · Ongoing Retainer",
    challenge: "US-based mid-market B2B firm operating without structured CRM. Pipeline invisible, sales teams had no common process.",
    metrics: [
      { value: "1", label: "unified HubSpot build" },
      { value: "9+", label: "months active retainer" },
      { value: "3", label: "workstreams running" },
    ],
    tags: ["HubSpot", "Data Enrichment", "Revenue Dashboards"],
    color: "#7C3AED",
  },
  {
    client: "Leading UI/UX Firm",
    type: "End-to-End Marketing & Sales Ops · 2-Year Programme",
    challenge: "Revenue stagnant for two years. Sales teams lacked structure, KPIs, and a system to generate or track pipeline.",
    metrics: [
      { value: "+40%", label: "more meetings booked" },
      { value: "+25%", label: "higher pipeline value" },
      { value: "+20%", label: "improvement in win rate" },
    ],
    tags: ["Brand Strategy", "Inbound Marketing", "Sales Structure"],
    color: "#00B4A0",
  },
  {
    client: "$200M Digital Engineering Firm",
    type: "RevOps Audit · Fixed-Scope Advisory",
    challenge: "31 ISRs averaging 9.5 meetings/year vs. 20–22 benchmark. 12-month ramp vs. 3–4 month standard. $32M pipeline gap.",
    metrics: [
      { value: "$32M", label: "pipeline gap identified" },
      { value: "13.7x", label: "peak ROI modelled" },
      { value: "2x", label: "pipeline ROI pathway" },
    ],
    tags: ["ISR Assessment", "ROI Modelling", "Restructuring Roadmap"],
    color: "#EF4444",
  },
  {
    client: "Global BPM Firm",
    type: "Maximise Existing + ABM-Led Growth · Ongoing",
    challenge: "HubSpot, LinkedIn Ads, Google Ads running in parallel — no consolidated pipeline attribution view. Thin marketing ops.",
    metrics: [
      { value: "3", label: "dedicated resources" },
      { value: "4", label: "active workstreams" },
      { value: "12+", label: "months as embedded partner" },
    ],
    tags: ["Attribution", "ABM", "CRM Governance"],
    color: "#F59E0B",
  },
  {
    client: "Boutique Consulting Firm",
    type: "Greenfield GTM · Professional Services",
    challenge: "Strong delivery capability but no ICP, no buyer journey, no SEO presence. Growth entirely referral-dependent.",
    metrics: [
      { value: "149", label: "prospect companies analysed" },
      { value: "4", label: "core GTM deliverables" },
      { value: "0→1", label: "referral to repeatable GTM" },
    ],
    tags: ["ICP Framework", "GTM Playbook", "SEO Architecture"],
    color: "#7C3AED",
  },
];

const BLOG_POSTS = [
  { title: "Pipeline Velocity: The RevOps Metric That Predicts Revenue Growth", slug: "pipeline-velocity-revops-metric-predicts-revenue-growth" },
  { title: "The Hidden Cost of RevOps Tool Sprawl", slug: "hidden-cost-revops-tool-sprawl" },
  { title: "Revenue Forecast Accuracy in RevOps", slug: "revenue-forecast-accuracy-revops" },
  { title: "CRM Data Hygiene & Sales Productivity", slug: "crm-data-hygiene-sales-productivity-2026-05-08" },
  { title: "AI Revenue Intelligence: Closing the RevOps Forecast Gap", slug: "ai-revenue-intelligence-closing-the-revops-forecast-gap" },
  { title: "AI Pipeline Forecasting for RevOps", slug: "ai-pipeline-forecasting-revops" },
  { title: "NRR: The North Star Metric for RevOps", slug: "nrr-north-star-revops-2026-04-21" },
  { title: "Pipeline Coverage Ratio: A Complete Guide", slug: "pipeline-coverage-ratio-guide" },
];

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <img src="/ryvr-logo.png" alt="RYVR" className="h-8 w-auto" />
          <div className="hidden md:flex items-center gap-6 text-sm text-gray-500">
            <a href="#services" className="hover:text-gray-900 transition-colors">Services</a>
            <a href="#case-studies" className="hover:text-gray-900 transition-colors">Case Studies</a>
            <a href="#blog" className="hover:text-gray-900 transition-colors">Blog</a>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/login")} className="text-sm text-gray-500 hover:text-gray-900 transition-colors hidden sm:block">
              Consultant login
            </button>
            <button
              onClick={() => navigate("/assess")}
              className="px-4 py-2 rounded-lg bg-[#00B4A0] hover:bg-[#009383] text-white text-sm font-medium transition-colors"
            >
              Start assessment
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#7C3AED]/20 bg-[#7C3AED]/5 text-[#7C3AED] text-xs font-mono mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[#7C3AED] animate-pulse" />
            Assessment → Analysis → Action Plan
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-gray-900">
            Know exactly where your<br />
            <span className="text-[#00B4A0]">revenue engine is leaking.</span>
          </h1>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto mb-10 leading-relaxed">
            A 10-minute assessment that scores your Brand, Marketing, and Revenue Operations
            against benchmarks for your company type, and tells you exactly what to fix first.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate("/assess")}
              className="flex items-center justify-center gap-2 h-12 px-8 bg-[#00B4A0] hover:bg-[#009383] text-white font-semibold rounded-xl transition-colors"
            >
              Take the free assessment <ArrowRight className="w-4 h-4" />
            </button>
            <a
              href={CAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 h-12 px-8 border border-gray-200 bg-white text-gray-700 hover:border-[#00B4A0] hover:text-[#00B4A0] rounded-xl transition-colors text-sm"
            >
              Book a consultant-guided session
            </a>
          </div>
          <p className="text-xs text-gray-400 mt-4">No login required · Results in under 10 minutes · Free</p>
          <div className="mt-6 pt-6 border-t border-gray-100">
            <button
              onClick={() => navigate("/whitepaper")}
              className="inline-flex items-center gap-2 text-sm text-[#7C3AED] hover:text-[#6D28D9] font-medium transition-colors"
            >
              <Download className="w-4 h-4" />
              Download the State of RevOps 2026 report — free
            </button>
          </div>
        </div>
      </section>

      {/* Whitepaper resource card */}
      <section className="px-6 pb-12">
        <div className="max-w-5xl mx-auto">
          <div className="rounded-2xl bg-[#0D0820] p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-[#7C3AED]/40 bg-[#7C3AED]/10 text-[#A78BFA] text-xs font-mono mb-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#A78BFA]" />
                State of RevOps 2026
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-2">
                How B2B, B2C &amp; D2C companies are building their revenue engine
              </h3>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-400">
                <span><span className="text-[#A78BFA] font-semibold">75%</span> of high-growth firms adopt RevOps by 2026</span>
                <span><span className="text-[#34D399] font-semibold">3%</span> average revenue uplift</span>
                <span><span className="text-[#FCD34D] font-semibold">10 pages</span> · free</span>
              </div>
            </div>
            <button
              onClick={() => navigate("/whitepaper")}
              className="flex-shrink-0 flex items-center gap-2 h-11 px-6 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold rounded-xl transition-colors text-sm whitespace-nowrap"
            >
              <Download className="w-4 h-4" />
              Download free
            </button>
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="py-20 px-6 border-t border-gray-100">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3 text-gray-900">Three scores. One clear picture.</h2>
            <p className="text-gray-500 max-w-xl mx-auto text-sm">
              Benchmarked against companies like yours. Not generic advice — specific gaps for your model.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Target, color: "#7C3AED", bg: "#7C3AED08", border: "#7C3AED20",
                title: "Brand", score: "Positioning",
                points: ["Brand positioning clarity", "Messaging framework", "Thought leadership", "Competitive differentiation"],
              },
              {
                icon: Zap, color: "#00B4A0", bg: "#00B4A008", border: "#00B4A025",
                title: "Marketing", score: "Demand & Attribution",
                points: ["Pipeline contribution", "Attribution accuracy", "ICP definition", "MarTech stack health"],
              },
              {
                icon: TrendingUp, color: "#F59E0B", bg: "#F59E0B08", border: "#F59E0B25",
                title: "Revenue Operations", score: "Pipeline & Process",
                points: ["CRM data quality", "Forecast accuracy", "Sales process rigor", "Retention & expansion"],
              },
            ].map(({ icon: Icon, color, bg, border, title, score, points }) => (
              <div key={title} className="p-6 rounded-2xl border" style={{ borderColor: border, background: bg }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 border" style={{ background: `${color}12`, borderColor: `${color}30` }}>
                  <Icon className="w-5 h-5" style={{ color }} />
                </div>
                <h3 className="font-bold text-lg mb-1 text-gray-900">{title}</h3>
                <p className="text-xs font-mono mb-4" style={{ color }}>{score}</p>
                <ul className="space-y-1.5">
                  {points.map(p => (
                    <li key={p} className="flex items-center gap-2 text-sm text-gray-500">
                      <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color }} />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-20 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-4">
            <span className="text-xs font-mono text-[#7C3AED] uppercase tracking-wider">How we work</span>
          </div>
          <div className="text-center mb-6">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Revenue Operations Strategy</h2>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto">
              We architect the revenue system. We build it. We stay in it.
            </p>
          </div>
          <p className="text-center text-sm text-gray-400 italic mb-14">
            Revenue predictability isn't a reporting problem. It's an architecture problem. We fix the architecture.
          </p>

          {/* 3 pillars */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {SERVICES.map(({ icon: Icon, color, title, tagline, deliverables }) => (
              <div key={title} className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: `${color}10` }}>
                  <Icon className="w-5 h-5" style={{ color }} />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed mb-4">{tagline}</p>
                <ul className="space-y-1.5">
                  {deliverables.map(d => (
                    <li key={d} className="flex items-start gap-2 text-xs text-gray-500">
                      <span className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0" style={{ background: color }} />
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Engagement models */}
          <div className="text-center mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-2">How We Engage</h3>
            <p className="text-sm text-gray-500">Scenarios can be combined. We scope each engagement to client stage, budget & growth objective.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {ENGAGEMENTS.map(({ num, title, sub, when, what, color }) => (
              <div key={title} className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                <div className="text-2xl font-bold mb-3" style={{ color }}>{num}</div>
                <h4 className="font-bold text-gray-900 mb-1">{title}</h4>
                <p className="text-xs font-mono mb-3" style={{ color }}>{sub}</p>
                <p className="text-xs text-gray-400 mb-4 leading-relaxed border-l-2 pl-3" style={{ borderColor: `${color}40` }}>{when}</p>
                <ul className="space-y-1.5">
                  {what.map(w => (
                    <li key={w} className="flex items-start gap-2 text-xs text-gray-600">
                      <ArrowRight className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color }} />
                      {w}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section id="case-studies" className="py-20 px-6 border-t border-gray-100">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-4">
            <span className="text-xs font-mono text-[#00B4A0] uppercase tracking-wider">Client results</span>
          </div>
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Case Studies</h2>
            <p className="text-gray-500 text-sm max-w-xl mx-auto">
              Real engagements. Measurable outcomes. Across B2B SaaS, consulting, digital engineering, and BPM.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {CASE_STUDIES.map(({ client, type, challenge, metrics, tags, color }) => (
              <div key={client} className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm flex flex-col">
                <div className="mb-4">
                  <div className="text-xs font-mono mb-1" style={{ color }}>{type}</div>
                  <h3 className="font-bold text-gray-900 text-sm">{client}</h3>
                </div>
                <p className="text-xs text-gray-500 leading-relaxed mb-4 flex-1">{challenge}</p>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {metrics.map(({ value, label }) => (
                    <div key={label} className="text-center p-2 rounded-lg" style={{ background: `${color}08` }}>
                      <div className="font-bold text-sm" style={{ color }}>{value}</div>
                      <div className="text-[10px] text-gray-400 leading-tight mt-0.5">{label}</div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1">
                  {tags.map(t => (
                    <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{t}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <a
              href={CAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 h-11 px-8 bg-[#00B4A0] hover:bg-[#009383] text-white font-semibold rounded-xl transition-colors text-sm"
            >
              Discuss your engagement <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Company types */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-3 text-gray-900">Built for your business model</h2>
          <p className="text-gray-500 text-sm mb-10 max-w-lg mx-auto">
            Benchmarks and recommendations differ by company type. Select yours and get a relevant score.
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            {COMPANY_TYPES.map(t => (
              <span key={t} className="px-3 py-1.5 rounded-full border border-gray-200 text-sm text-gray-600 bg-white">{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="py-20 px-6 border-t border-gray-100">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-3 text-gray-900">Designed for your role</h2>
          <p className="text-gray-500 text-sm mb-10 max-w-lg mx-auto">
            The dashboard adapts to your perspective — different priorities for different roles.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { role: "Founder / CEO", focus: "Full revenue engine health + strategic gaps" },
              { role: "CMO", focus: "Brand + marketing effectiveness + pipeline contribution" },
              { role: "Sales Head", focus: "Pipeline, process, forecast + CRM health" },
              { role: "Product Head", focus: "PLG motion, activation + expansion revenue" },
              { role: "Category Head", focus: "Channel mix, pricing power + market positioning" },
            ].map(({ role, focus }) => (
              <div key={role} className="p-4 rounded-xl border border-gray-100 bg-white text-left shadow-sm">
                <div className="flex items-center gap-1.5 mb-2">
                  <Users className="w-3.5 h-3.5 text-[#00B4A0]" />
                  <span className="text-xs font-semibold text-gray-900">{role}</span>
                </div>
                <p className="text-[11px] text-gray-400 leading-relaxed">{focus}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog */}
      <section id="blog" className="py-20 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-4">
            <span className="text-xs font-mono text-[#7C3AED] uppercase tracking-wider">Insights</span>
          </div>
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">From the Blog</h2>
            <p className="text-gray-500 text-sm max-w-lg mx-auto">
              GTM engineering, RevOps frameworks, and AI infrastructure — from the RYVR team.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
            {BLOG_POSTS.map(({ title, slug }) => (
              <a
                key={slug}
                href={`https://ryvr.in/blog/${slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:border-[#7C3AED]/30 hover:shadow-md transition-all group flex items-center justify-between gap-4"
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#7C3AED]/8 flex items-center justify-center flex-shrink-0">
                    <BarChart2 className="w-4 h-4 text-[#7C3AED]" />
                  </div>
                  <p className="text-sm font-semibold text-gray-900 group-hover:text-[#7C3AED] transition-colors leading-snug">{title}</p>
                </div>
                <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-[#7C3AED] flex-shrink-0 transition-colors" />
              </a>
            ))}
          </div>
          <div className="text-center">
            <a
              href="https://ryvr.in/blog"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-[#7C3AED] hover:text-[#6D28D9] font-medium transition-colors"
            >
              View all articles on ryvr.in <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 border-t border-gray-100">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-gray-900">
            Ready to find out where your <span className="text-[#00B4A0]">revenue engine stands?</span>
          </h2>
          <p className="text-gray-500 mb-8">Free, 10 minutes, no login required. Results delivered instantly.</p>
          <button
            onClick={() => navigate("/assess")}
            className="flex items-center gap-2 mx-auto h-12 px-10 bg-[#00B4A0] hover:bg-[#009383] text-white font-semibold rounded-xl transition-colors"
          >
            Start your assessment <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-gray-100">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-gray-400">
          <div className="flex items-center gap-6">
            <span>© {new Date().getFullYear()} RYVR · revops.ryvr.in</span>
            <a href="#services" className="hover:text-gray-700 transition-colors">Services</a>
            <a href="#case-studies" className="hover:text-gray-700 transition-colors">Case Studies</a>
            <a href="https://ryvr.in/blog" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700 transition-colors">Blog</a>
          </div>
          <a href="mailto:jigesh@ryvr.in" className="hover:text-gray-700 transition-colors">jigesh@ryvr.in</a>
        </div>
      </footer>
    </div>
  );
}
